// Cierre de sesión compartido por "Mi cuenta" y el menú de usuario de la barra superior: si quedan
// archivos sin subir en este dispositivo, pide confirmación antes de salir.
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { exportLocalWork, localWorkSummary } from '../../local/export.ts';
import { Button } from '../../ui/Button.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { useNotices } from '../../ui/notices-context.ts';
import { useAuth, useProfile } from '../auth/auth-context.ts';

export function localWorkKey(userId: string) {
  return ['local-work', userId] as const;
}

export function useExportLocalWork() {
  const profile = useProfile();
  const notices = useNotices();
  return async function exportWork() {
    const count = await exportLocalWork(profile.id).catch(() => -1);
    if (count < 0) notices.error('No se pudo exportar el trabajo local.');
    else notices.info(`Se descargaron ${count} elemento(s) guardados en este dispositivo.`);
  };
}

export function useLogout() {
  const profile = useProfile();
  const { signOut } = useAuth();
  const queryClient = useQueryClient();
  const exportWork = useExportLocalWork();
  const [pendingFiles, setPendingFiles] = useState(0);

  async function requestLogout() {
    const summary = await queryClient
      .fetchQuery({ queryKey: localWorkKey(profile.id), queryFn: () => localWorkSummary(profile.id) })
      .catch(() => null);
    const files = summary?.files ?? 0;
    if (files > 0) setPendingFiles(files);
    else await signOut();
  }

  const close = () => setPendingFiles(0);
  const dialog = (
    <Dialog
      open={pendingFiles > 0}
      alert
      title="Archivos sin subir"
      onClose={close}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancelar
          </Button>
          <Button variant="secondary" onClick={() => void exportWork()}>
            Exportar primero
          </Button>
          <Button variant="danger" onClick={() => void signOut()}>
            Cerrar sesión igualmente
          </Button>
        </>
      }
    >
      <p>
        Tienes {pendingFiles} archivo(s) que todavía no se subieron. Se conservarán en este dispositivo y se subirán
        cuando vuelvas a ingresar con esta misma cuenta; otra persona que use este teléfono no podrá verlos.
      </p>
    </Dialog>
  );

  return { requestLogout, dialog };
}
